import Product from '../../domains/Product'

import type { GroupedLineItemData } from './cart.service.b2b.types'
import type {
  AddToCartRequest as B2BAddToCartRequest,
  CreateCartRequest as B2BCreateCartRequest,
} from './cart.service.request.types'
import type { InventoryResponse } from '../inventoryService/inventory.service'

export enum cartStatus {
  COMPLETE = 'COMPLETE',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum DiscountCodeApplicationInfoStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
  INELIGIBLE = 'INELIGIBLE',
}

interface DiscountCodeApplicationInfo {
  status: DiscountCodeApplicationInfoStatus
  discountAmount: number
  reasonCode?: string | null
  metadata?: unknown
}

interface CartCostSummary {
  discountCodeApplicationInfo: DiscountCodeApplicationInfo
  subTotalAmount: number
  subTotalAmountToDisplay?: number
  totalDiscount: number
  totalDiscountToDisplay?: number
  totalStoreCreditGrant: number
  totalAmount: number
}

export interface SimplifiedVariant {
  id: number
  title: string
  price: number
  options: { name: string; value: string }[]
  /**
   * True when the product catalog includes a `customization` object on this
   * variant (embroidery, monogramming, etc.). Variants that do not offer
   * customization omit the field entirely (not `null`), so this is derived at
   * cart enrich time via field-presence on the catalog ProductVariant.
   */
  catalogOffersCustomization?: boolean
}

// Subscription-related types
export enum PurchaseMode {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export interface SubscriptionDetail {
  customerSubscriptionId: number | null
  subscriptionPlanId: number
  maxQuantityAllowed: number
}

export interface SubscriptionFrequency {
  id: number
  frequencyCode: string | null
  unit: string
  interval: number
  displayLabel: string
}

export interface FrequencyOption {
  planId: number
  label: string
  discountPercent: number
  isDefault?: boolean
}

export interface SubscriptionPlan {
  id: number
  frequency: SubscriptionFrequency
  isDefault: boolean
  maxAllowedQuantity: number
  benefitMode: string
  benefitValue: number
  benefitValueType: string
  updatedBy: string
  updatedAt: string
}

export interface Cart {
  id: string
  customerId?: string
  lineItems: CartLineItem[]
  saveForLaterItems?: CartLineItem[]
  outOfStockLineItems?: CartLineItem[]
  containsGroupLineItem?: boolean | null
  groupedLineItems?: GroupedLineItemData<CartLineItem>[] | null
  currencyCode: string
  discountCode?: string
  storeProgramId?: number | null
  type?: string | null
  regionId?: number | null
  cartStatus: cartStatus
  cartCostSummary?: CartCostSummary
  createdAt: string
  updatedAt: string
  giftingDetails?: GiftingDetails
  isGift?: boolean
  isDateOfBirthInformationAvailable?: boolean
  isDateOfBirthRequired?: boolean
  isAgeRestrictedCart?: boolean
  giftingDetailsTtl?: string | null
  parentCartId?: string | null
  checkoutId?: string | null
  allocationType?: string | null
  lineOfBusiness?: string | null
  paymentMode?: string | null
  customerDateOfBirth?: string | null
  recurringItemsPresent?: boolean
  promoAllocationSummary?: unknown
  promoApplicationError?: unknown
  hash?: string | null
  allVariantsInventory?: Record<number, InventoryResponse>
  allProductVariants?: Record<string, SimplifiedVariant[]>
}

export interface CartAttribute {
  key: string
  value: string
}

export enum PromoType {
  STORE_CREDIT_GRANT = 'STORE_CREDIT_GRANT',
  MONEY_DISCOUNT = 'MONEY_DISCOUNT',
  SUBSCRIPTION_DISCOUNT = 'SUBSCRIPTION_DISCOUNT',
  SUBSCRIPTION_STORE_CREDIT = 'SUBSCRIPTION_STORE_CREDIT',
}

export interface PromoAllocation {
  amount: number
  promoCode: string
  promoType: PromoType
  promoId?: number
  promoApplicationType?: 'AUTOMATIC' | 'MANUAL'
  percentageAmount?: number
}
interface CartLineItemCostSummary {
  promoAllocations: PromoAllocation[]
  amountPerQuantity: number
  subtotalAmount?: number
  totalDiscount?: number
  totalStoreCreditGrant?: number
  totalAmount?: number
}

/**
 * Smart Variant / VDA v2 — per-attribute metadata on a line-item variant.
 * `variantMetaData.attributes[]` and `variantMetaData.attributeValues{}` replace
 * the legacy `variationAttributeByKeyMap` when the BE contract is upgraded.
 */
export interface VariantAttributeVdaConfig {
  order?: number | null
  searchTileable?: boolean | null
}

export interface VariantAttribute {
  key: string
  title: string
  isVda: boolean
  /** ECOMP-9742: VDA display order ships as vdaConfig.order. */
  vdaConfig?: VariantAttributeVdaConfig | null
  type: string
  searchTileable?: boolean
}

export interface VariantAttributeValueDisplay {
  value: string
  localisedValue: string
}

export interface VariantAttributeValue {
  type: string
  displayValue: VariantAttributeValueDisplay
  sortOrder?: number | null
  swatchType?: string | null
  hexCode?: string | null
  swatchImageUrl?: string | null
  imageUrl?: string | null
}

interface VariantMetaData {
  variantId: number
  productId: number
  price: number
  imageUrl: string
  productTitle: string
  variantTitle: string
  sku?: string
  variationAttribute?: Record<string, string> | null
  /**
   * Smart Variant / VDA v2. Per-attribute descriptor for this variant, keyed by
   * the attribute key (e.g. `size`, `color`). Each entry carries `{title,
   * value, type}` — `value` is the display string (already the correct locale
   * for the caller). Show-bucket B2B grouped renderers read the diff-attribute
   * value from `variationAttributeByVariationTitleMap[differingVariantAttributeBreakdownList.key].value`
   * instead of parsing `variantTitle`.
   */
  variationAttributeByVariationTitleMap?: Record<string, { title: string; value: string; type: string }> | null
  /**
   * Smart Variant / VDA v2. Metadata for every attribute on this variant. Under
   * the show bucket of the ramp_svd-rollout-web experiment (useIsSvdEnabled), callers iterate `attributes` where
   * `isVda === true` in `vdaConfig.order` order and read `localisedValue`
   * from `attributeValues[key].displayValue` to build the variant title.
   */
  attributes?: VariantAttribute[]
  attributeValues?: Record<string, VariantAttributeValue>
  additionalAttributes?: Record<string, string> | null
  regionId?: number | null
  locale?: string | null
  spid?: number | null
  svid?: number | null
  taxCode?: string | null
  height?: number | null
  length?: number | null
  width?: number | null
  weight?: number | null
  isNonPhysicalProduct?: boolean | null
  isLiveInWebsite?: boolean | null
  departmentId?: number | null
  productDivisionId?: number | null
  vendorSkuDetails?: { vendorCode: string; vendorSKU: string }[] | null
  isSubscribable?: boolean | null
  plans?: SubscriptionPlan[]
  subscriptionMaxQuantity?: number
}

export interface ImageOverrideDetails {
  overrideImageUrl?: string | null
  fileId?: string | null
  fileMetaData?: { fileName: string } | null
}

export interface CartLineItem {
  id: string
  variantId: number
  quantity: number
  attributes: CartAttribute[]
  customizationAttributes?: CartAttribute[]
  costSummary?: CartLineItemCostSummary
  variantMetaData?: VariantMetaData
  createdAt: string
  updatedAt: string
  latestAddedToCartAt?: string | null
  totalLineItemQuantity: number
  productMetaData?: ProductMetaData
  inventoryData?: InventoryData
  inventoryStatus?: 'IN_STOCK' | 'OUT_OF_STOCK' | 'PARTIAL_OUT_OF_STOCK'
  inventoryType?: string | null
  outOfStockQuantity?: number | null
  isActive?: boolean
  deactivationReasonCode?: string | null
  poId?: number | null
  cartLineItemType?: string
  isCustomization?: boolean | null
  isCustomized?: boolean | null
  groupId?: string | null
  imageOverrideDetails?: ImageOverrideDetails | null
  customizationDetails?: Record<string, string> | null
  customizationType?: string | null
  slug?: string
  // Subscription-related fields
  purchaseMode?: PurchaseMode
  subscriptionDetail?: SubscriptionDetail
}

interface ProductMetaData {
  svid: string | number
  spid: string | number
  productId: string | number
  options: {
    name: string
    value: string
  }[]
}

interface InventoryData {
  available: number
  availableForCustomization?: number
  tags: {
    preOrderShippingDate: string
  }
}

export interface LineItem {
  variantId: number | string
  quantity: number
  attributes: CartAttribute[]
  subscriptionPlanId?: number
}

export type LineItems = LineItem[]
export type AddItemToCartRequest = B2BAddToCartRequest<LineItem>
export type CreateCartRequest = B2BCreateCartRequest<LineItem>
export interface TBuyNowApiResponse {
  buyNowCartAuthMapping: {
    id: string
    authCode: string
  }
  parentCartAuthMapping: {
    id: string
    authCode: string
  }
  buyNowCartCheckoutMapping: {
    cartId: string
    checkoutId: string
    checkoutAuthCode: string
  }
}

export interface GiftingDetails {
  recipientName: string
  recipientEmail: string
  senderName: string
  giftMessage: string
}
export type { GroupSummaryData, GroupedLineItemData, GroupedVariantAttribute } from './cart.service.b2b.types'

export interface BulkOrderCustomizationLineItem {
  variantId: number
  quantity: number
  attributes?: CartAttribute[]
}

export interface AddBulkOrderCustomizationInput {
  product: Product
  bulkLineItemDetails: BulkOrderCustomizationLineItem[]
  customizationDetails?: unknown
  openCartPopupOnSuccess?: boolean
  extraParams?: Record<string, unknown>
}
